import { useState, useEffect } from "react";
import { CheckCircle, ArrowRight, Linkedin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useConsultationEnabled } from "@/hooks/useConsultationEnabled";

interface TeamMember {
  id: string;
  name: string;
  title: string;
  credentials: string | null;
  bio: string;
  photo_url: string;
  linkedin_url: string | null;
  display_order: number;
}

const About = () => {
  const consultationEnabled = useConsultationEnabled();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    const loadTeamMembers = async () => {
      const { data } = await supabase
        .from('team_members')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (data) {
        setTeamMembers(data);
      }
    };
    loadTeamMembers();
  }, []);

  const expertise = [
    "Clear, practical advice without the jargon",
    "Decades of combined experience in information security",
    "Deep understanding of business needs and compliance",
    "Proven track record across diverse sectors",
    "Strategic thinking backed by hands-on expertise",
    "Focus on sustainable, practical solutions",
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us - Expert Cyber Security Team | 4FOX Security"
        description="Learn about 4FOX Security's team of cyber security experts. Decades of combined experience in information security, governance, and practical business protection."
        canonical="https://4fox.uk/about"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About 4FOX Security",
          "description": "About page for 4FOX Security cyber consultancy",
          "url": "https://4fox.uk/about",
          "mainEntity": {
            "@type": "Organisation",
            "name": "4FOX Security",
            "founder": [
              {
                "@type": "Person",
                "name": "Hazel McPherson",
                "jobTitle": "Founder & CEO",
                "sameAs": "https://linkedin.com/in/hazelmcpherson"
              },
              {
                "@type": "Person",
                "name": "Andy Yam",
                "jobTitle": "Founding Director",
                "sameAs": "https://linkedin.com/in/andyyam"
              }
            ]
          }
        }}
      />
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Making Security Accessible to Everyone
              </h1>
              <p className="text-xl text-muted-foreground">
                Expert cyber security guidance for growing businesses
              </p>
            </div>

            {/* Mission & Expertise Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="shadow-lg">
                <CardContent className="pt-6">
                  <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
                  <p className="text-muted-foreground mb-4">
                    We believe that effective cyber security shouldn't be complicated or overwhelming. 
                    Our mission is to make robust security accessible to businesses of all sizes, 
                    providing the expertise and guidance you need without the complexity.
                  </p>
                  <p className="text-muted-foreground mb-4">
                    We don't just implement security measures – we help you understand them, 
                    own them, and integrate them naturally into your business operations.
                  </p>
                  <p className="text-muted-foreground">
                    Whether you need strategic leadership through fractional CISO services, 
                    compliance guidance, or hands-on security implementation, we're here to help 
                    you build a security posture that fits your business.
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold mb-4">What Sets Us Apart</h3>
                  <ul className="space-y-3">
                    {expertise.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Approach Section */}
            <Card className="mb-16 shadow-lg">
              <CardContent className="pt-8">
                <h2 className="text-3xl font-bold mb-6 text-center">Our Approach</h2>
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  <p className="mb-4">
                    We take a pragmatic, business-focused approach to cyber security. Rather than 
                    overwhelming you with technical complexity, we focus on what matters most to 
                    your organisation – protecting your assets, maintaining customer trust, and 
                    meeting compliance requirements.
                  </p>
                  <p className="mb-4">
                    Our team combines deep technical expertise with real-world business experience. 
                    We've worked across numerous sectors, from finance to healthcare, and understand 
                    the unique security challenges each industry faces.
                  </p>
                  <p>
                    We're not here to sell you expensive solutions you don't need. We're here to 
                    help you build security that's proportionate, sustainable, and actually works 
                    for your business.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Leadership Section */}
            {teamMembers.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold mb-8 text-center">Meet Our Leadership</h2>
                <div className={`grid gap-8 ${teamMembers.length === 1 ? 'max-w-md mx-auto' : 'md:grid-cols-2'}`}>
                  {teamMembers.map((member) => (
                    <Card key={member.id} className="transition-all duration-300 hover:shadow-lg">
                      <CardContent className="pt-6">
                        <div className="flex flex-col items-center text-center">
                          <img
                            src={member.photo_url}
                            alt={`${member.name} - ${member.title}`}
                            className="w-[150px] h-[150px] rounded-full object-cover mb-4"
                          />
                          <h3 className="text-2xl font-bold mb-1">{member.name}</h3>
                          <p className="text-accent font-semibold mb-2">{member.title}</p>
                          {member.credentials && (
                            <p className="text-sm text-muted-foreground mb-4">
                              {member.credentials}
                            </p>
                          )}
                          <p className="text-muted-foreground mb-4">
                            {member.bio}
                          </p>
                          {member.linkedin_url && (
                            <a
                              href={member.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                            >
                              <Linkedin className="w-5 h-5" />
                              <span>Connect on LinkedIn</span>
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div className="text-center">
              <div className="p-8 bg-secondary/30 rounded-lg">
                <h2 className="text-3xl font-bold mb-4">Ready to Strengthen Your Security?</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Let's discuss how we can help protect your business with practical, effective cyber security.
                </p>
                {consultationEnabled && (
                <a 
                  href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-accent hover:bg-accent/90">
                    Book a Free Consultation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
